import { memo, useEffect, useMemo, useState } from 'react';

import { CompositeFilterDescriptor, FilterDescriptor, State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { FilterChangeEvent } from '@progress/kendo-react-data-tools';
import styled from 'styled-components';

import { TagModel, WorklistViewModel } from 'models';

import { useEvent } from 'core/hooks';
import { NotificationsService } from 'core/notifications';
import { Button, ButtonVariants, ColumnState, Window } from 'core/ui';
import { Filter } from 'core/ui/Filter';

import { DefaultColumnsState } from 'features/exam/constants';
import { ExamsGridService } from 'features/exam/services/exam-grid-service';
import { TagsField } from 'features/tags/fragments/TagsField';
import { useTagLookups } from 'features/tags/hooks/useTagLookups';
import { RelativeDateFilter } from './RelativeDateFilter';
import { RelativeDateFilterField, RelativeDateFilterType } from 'features/worklist/utils/relative-date-filters';

import { WorklistEditService } from '../services';
import { WorklistColumnSelectionChange } from '../types';
import { WorklistEditModalProps } from '../types/WorklistEditModalProps';
import { WorklistColumnSelection } from './WorklistColumnSelection';

// Get the actual field definitions from the exam grid service
const ALL_COLUMN_DEFINITIONS = ExamsGridService.getColumns();
const ALL_GRID_COLUMN_DEFINITIONS = ExamsGridService.getGridColumns();
const ALL_FIELDS = ALL_COLUMN_DEFINITIONS.filter((column) => column.filter).map((column) => ({
  filter: column.filter,
  label: column.title,
  name: column.field,
  operators: column.operators ?? [],
}));

export const WorklistEditModal = memo<WorklistEditModalProps>(({ worklist, onClose, onSave, filterType = 'field' }) => {
  // Hook for tag lookups - needed for PatientFile entities
  const { allHierarchicalTags, allFlatTags, isLoadingTags, tagIdToTagMap } = useTagLookups();

  // Parse the existing filter from dataState using the proper service
  const initialFilter = useMemo(() => {
    const filter = WorklistEditService.initializeWorklistEditModalDataState(worklist.dataState);
    console.log('Initialized filter from WorklistEditService:', filter);
    return filter;
  }, [worklist.dataState]);

  // Parse the existing column state or use defaults
  const initialColumnsState = useMemo(() => {
    try {
      return worklist.columnState ? JSON.parse(worklist.columnState) : DefaultColumnsState;
    } catch (error) {
      console.warn('Failed to parse column state, using defaults:', error);
      return DefaultColumnsState;
    }
  }, [worklist.columnState]);

  const [worklistName, setWorklistName] = useState(worklist.name);
  const [filterForm, setFilterForm] = useState<CompositeFilterDescriptor>(initialFilter);
  const [columnsStateForm, setColumnsStateForm] = useState<Record<string, ColumnState>>(initialColumnsState);
  const [selectedTags, setSelectedTags] = useState<TagModel[]>([]);

  // Relative date filter state
  const [relativeDateFilterField, setRelativeDateFilterField] = useState<RelativeDateFilterField | null>(null);
  const [relativeDateFilterType, setRelativeDateFilterType] = useState<RelativeDateFilterType | null>(null);

  // Initialize selected tags when tags are loaded (for PatientFile entities)
  useEffect(() => {
    if (worklist.entity === 'PatientFile' && worklist.oDataResourceString && allFlatTags.length > 0) {
      const tagIdMatch = /TagId~in~([\d,]+)/.exec(worklist.oDataResourceString);
      if (tagIdMatch) {
        const tagIds = tagIdMatch[1]
          .split(',')
          .map(Number)
          .filter((id) => !isNaN(id));
        const initialSelectedTagModels = tagIds.map((id) => tagIdToTagMap.get(id)).filter((tag): tag is TagModel => tag !== undefined);
        setSelectedTags(initialSelectedTagModels);
        console.log('[WorklistEditModal] Initialized selectedTags from worklist:', initialSelectedTagModels);
      }
    }
  }, [worklist.entity, worklist.oDataResourceString, allFlatTags, tagIdToTagMap]);

  // Initialize relative date filter from worklist dataState (for Exam entities)
  useEffect(() => {
    if (worklist.entity === 'Exam' && worklist.dataState) {
      try {
        const dataState = JSON.parse(worklist.dataState);
        if (dataState.relativeDateFilters && Array.isArray(dataState.relativeDateFilters) && dataState.relativeDateFilters.length > 0) {
          const firstFilter = dataState.relativeDateFilters[0];
          setRelativeDateFilterField(firstFilter.field);
          setRelativeDateFilterType(firstFilter.filterType);
          console.log('[WorklistEditModal] Initialized relative date filter from worklist:', firstFilter);
        }
      } catch (error) {
        console.warn('Failed to parse worklist dataState for relative date filters:', error);
      }
    }
  }, [worklist.entity, worklist.dataState]);

  // Check entity types
  const isExamEntity = worklist.entity === 'Exam';
  const isPatientFileEntity = worklist.entity === 'PatientFile';

  // Handle filter change
  const handleFilterChange = useEvent((event: FilterChangeEvent) => {
    const newFilterForm: CompositeFilterDescriptor = {
      ...filterForm,
      ...event.filter,
    };
    setFilterForm(newFilterForm);
  });

  // Handle columns change
  const handleColumnsChange = useEvent((changes: WorklistColumnSelectionChange[]) => {
    const newColumnsState = { ...columnsStateForm };
    changes.forEach((change) => {
      if (change.column?.field && change.columnState) {
        newColumnsState[change.column.field] = change.columnState;
      }
    });
    setColumnsStateForm(newColumnsState);
  });

  // Handle relative date filter changes
  const handleRelativeDateFilterChange = useEvent((field: RelativeDateFilterField, filterType: RelativeDateFilterType) => {
    setRelativeDateFilterField(field);
    setRelativeDateFilterType(filterType);
  });

  const handleRelativeDateFilterClear = useEvent(() => {
    setRelativeDateFilterField(null);
    setRelativeDateFilterType(null);
  });

  // Handle save click
  const handleSaveClick = useEvent(() => {
    let newDataState: State;
    let updatedWorklist: WorklistViewModel;

    if (isPatientFileEntity) {
      // Handle PatientFile entity with tags
      const currentSelectedTagIds = selectedTags.map((tag) => tag.id);

      let originalKendoState: State = {};
      if (worklist.dataState) {
        try {
          originalKendoState = JSON.parse(worklist.dataState);
        } catch (e) {
          console.error('Error parsing existing worklist.dataState:', e);
          originalKendoState = { filter: { logic: 'and', filters: [] } };
        }
      }

      if (!originalKendoState.filter || typeof originalKendoState.filter !== 'object') {
        originalKendoState.filter = { logic: 'and', filters: [] };
      } else {
        if (!Array.isArray(originalKendoState.filter.filters)) {
          originalKendoState.filter.filters = [];
        }
        if (typeof originalKendoState.filter.logic !== 'string') {
          originalKendoState.filter.logic = 'and';
        }
      }

      // Remove existing TagId filters
      const baseFilters = (originalKendoState.filter?.filters || []).filter((f) => {
        if (typeof f === 'object' && f && 'field' in f && typeof f.field === 'string') {
          return f.field !== 'TagId';
        }
        return true;
      });

      const activeFilters: (FilterDescriptor | CompositeFilterDescriptor)[] = [...baseFilters];

      if (currentSelectedTagIds.length > 0) {
        activeFilters.push({
          field: 'TagId',
          operator: 'in',
          value: currentSelectedTagIds,
        });
      }

      const newFilterDescriptor: CompositeFilterDescriptor | undefined =
        activeFilters.length > 0 ? { logic: originalKendoState.filter?.logic || 'and', filters: activeFilters } : undefined;

      newDataState = {
        ...originalKendoState,
        filter: newFilterDescriptor,
      };

      updatedWorklist = {
        ...worklist,
        name: worklistName,
        dataState: JSON.stringify(newDataState),
        columnState: JSON.stringify(columnsStateForm),
        oDataResourceString: toDataSourceRequestString(newDataState),
      };
    } else {
      // Handle Exam entity with filters and columns
      newDataState = {
        filter: filterForm,
        sort: [],
      };

      // Add relative date filter to dataState if specified
      if (relativeDateFilterField && relativeDateFilterType) {
        const config = ExamsGridService.createRelativeDateFilterConfig(relativeDateFilterField, relativeDateFilterType);
        const updatedDataStateJson = ExamsGridService.saveRelativeDateFilterToWorklist(JSON.stringify(newDataState), config);
        newDataState = JSON.parse(updatedDataStateJson);
      }

      updatedWorklist = {
        ...worklist,
        name: worklistName,
        dataState: JSON.stringify(newDataState),
        columnState: JSON.stringify(columnsStateForm),
      };
    }

    onSave(updatedWorklist);
    NotificationsService.displaySuccess(`Worklist ${worklistName} saved`);
    onClose();
  });

  return (
    <Window onClose={onClose} title="EDIT WORKLIST" initialWidth={800} initialHeight={600}>
      <StyledDialogBodyDiv>
        <StyledWorklistNameDiv>
          <StyledLabel>Worklist Name:</StyledLabel>
          <StyledInput type="text" value={worklistName} onChange={(e) => setWorklistName(e.target.value)} />
        </StyledWorklistNameDiv>

        {isPatientFileEntity ? (
          <StyledTagsSection>
            <StyledSectionHeader>TAGS</StyledSectionHeader>
            <StyledTagsWrapper>
              {isLoadingTags ? (
                <div>Loading tags...</div>
              ) : (
                <TagsField
                  tags={allHierarchicalTags}
                  value={selectedTags}
                  onChange={setSelectedTags}
                  label="Select tags for this worklist"
                  placeholder="Search or select tags..."
                />
              )}
            </StyledTagsWrapper>
          </StyledTagsSection>
        ) : (
          <>
            <StyledFiltersSection>
              <StyledSectionHeader>FILTERS</StyledSectionHeader>
              <StyledFiltersWrapper>
                <Filter fields={ALL_FIELDS} value={filterForm} onChange={handleFilterChange} />
              </StyledFiltersWrapper>
            </StyledFiltersSection>

            {isExamEntity && (
              <StyledRelativeDateSection>
                <StyledSectionHeader>RELATIVE DATE FILTER</StyledSectionHeader>
                <StyledRelativeDateWrapper>
                  <RelativeDateFilter
                    selectedField={relativeDateFilterField}
                    selectedFilterType={relativeDateFilterType}
                    onFilterChange={handleRelativeDateFilterChange}
                    onClearFilter={handleRelativeDateFilterClear}
                  />
                </StyledRelativeDateWrapper>
              </StyledRelativeDateSection>
            )}

            {isExamEntity && (
              <StyledColumnsSection>
                <StyledSectionHeader>COLUMNS</StyledSectionHeader>
                <StyledColumnsWrapper>
                  <WorklistColumnSelection columns={ALL_GRID_COLUMN_DEFINITIONS} columnsState={columnsStateForm} onChange={handleColumnsChange} />
                </StyledColumnsWrapper>
              </StyledColumnsSection>
            )}
          </>
        )}

        <StyledButtonsDiv>
          <Button variant={ButtonVariants.SECONDARY} onClick={onClose}>
            Cancel
          </Button>
          <Button variant={ButtonVariants.PRIMARY} onClick={handleSaveClick}>
            Save
          </Button>
        </StyledButtonsDiv>
      </StyledDialogBodyDiv>
    </Window>
  );
});

WorklistEditModal.displayName = 'WorklistEditModal';

const StyledDialogBodyDiv = styled.div`
  padding: ${({ theme }) => theme.space.spacing40};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.spacing40};
  height: 100%;
`;

const StyledWorklistNameDiv = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.spacing20};
`;

const StyledLabel = styled.label`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  min-width: 120px;
`;

const StyledInput = styled.input`
  flex: 1;
  padding: ${({ theme }) => theme.space.spacing20};
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[4]};
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSizes.body};
`;

const StyledFiltersSection = styled.div`
  flex: 1;
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[4]};
  border-radius: 4px;
  overflow: hidden;
`;

const StyledColumnsSection = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[4]};
  border-radius: 4px;
  overflow: hidden;
`;

const StyledTagsSection = styled.div`
  flex: 1;
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[4]};
  border-radius: 4px;
  overflow: hidden;
`;

const StyledRelativeDateSection = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.palette.grayscale[4]};
  border-radius: 4px;
  overflow: hidden;
`;

const StyledSectionHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.palette.grayscale[1]};
  padding: ${({ theme }) => theme.space.spacing20};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grayscale[4]};
`;

const StyledFiltersWrapper = styled.div`
  padding: ${({ theme }) => theme.space.spacing20};
  min-height: 400px;
`;

const StyledColumnsWrapper = styled.div`
  padding: ${({ theme }) => theme.space.spacing20};
  max-height: 400px;
  overflow-y: auto;
`;

const StyledTagsWrapper = styled.div`
  padding: ${({ theme }) => theme.space.spacing20};
  min-height: 400px;
`;

const StyledRelativeDateWrapper = styled.div`
  padding: ${({ theme }) => theme.space.spacing20};
`;

const StyledButtonsDiv = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.space.spacing20};
  padding-top: ${({ theme }) => theme.space.spacing20};
  border-top: 1px solid ${({ theme }) => theme.colors.palette.grayscale[4]};
`;
